import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

function ModalFirma({ label, onGuardar, onCancelar }) {
  const canvasRef = useRef(null);
  const dibujando = useRef(false);
  const lastPos = useRef(null);
  const [vacio, setVacio] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#0f6e56';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    dibujando.current = true;
    lastPos.current = getPos(e, canvasRef.current);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!dibujando.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    if (vacio) setVacio(false);
  };

  const endDraw = (e) => {
    e.preventDefault();
    dibujando.current = false;
  };

  const limpiar = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setVacio(true);
  };

  const guardar = () => {
    if (vacio) return;
    const dataURL = canvasRef.current.toDataURL('image/png');
    onGuardar(dataURL);
  };

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 16px',
        background: '#0B0F0D',
        borderBottom: '1px solid #24312B',
        flexShrink: 0,
      }}>
        <button type="button" onClick={onCancelar} style={{
          background: 'none', border: 'none', color: '#8A9994',
          fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '4px 8px',
        }}>
          Cancelar
        </button>
        <span style={{ color: '#F4F7F5', fontSize: 15, fontWeight: 700 }}>
          {label}
        </span>
        <button type="button" onClick={limpiar} style={{
          background: 'none', border: 'none', color: '#EF4444',
          fontSize: 14, fontWeight: 600, cursor: 'pointer', padding: '4px 8px',
        }}>
          Limpiar
        </button>
      </div>

      {/* Instrucción */}
      <div style={{
        textAlign: 'center', color: '#8A9994', fontSize: 13,
        padding: '12px 16px 8px', flexShrink: 0,
        background: '#0B0F0D',
      }}>
        Dibuja tu firma en el recuadro blanco
      </div>

      {/* Canvas */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
        background: '#0B0F0D',
      }}>
        <div style={{
          position: 'relative',
          width: '100%', maxWidth: 640,
          borderRadius: 12,
          overflow: 'hidden',
          border: `2px solid ${vacio ? '#2E4A40' : '#0f6e56'}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
        }}>
          <canvas
            ref={canvasRef}
            width={640}
            height={280}
            style={{
              width: '100%',
              height: 220,
              display: 'block',
              background: 'white',
              touchAction: 'none',
              cursor: 'crosshair',
            }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          {vacio && (
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              color: '#ccc', fontSize: 15, pointerEvents: 'none',
              textAlign: 'center', lineHeight: 1.6,
            }}>
              ✍ Firma aquí
            </div>
          )}
        </div>
      </div>

      {/* Guardar */}
      <div style={{
        padding: '16px', flexShrink: 0,
        background: '#0B0F0D',
        borderTop: '1px solid #24312B',
      }}>
        <button
          type="button"
          onClick={guardar}
          disabled={vacio}
          style={{
            width: '100%', padding: '14px',
            background: vacio ? '#2A332F' : '#0F6E56',
            color: vacio ? '#4D6358' : 'white',
            border: 'none', borderRadius: 10,
            fontSize: 15, fontWeight: 700, cursor: vacio ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
        >
          Guardar firma
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default function FirmaCanvas({ label, onChange }) {
  const [modalAbierto, setModalAbierto] = useState(false);
  const [firmada, setFirmada] = useState(false);

  const handleGuardar = (dataURL) => {
    setFirmada(true);
    setModalAbierto(false);
    onChange(dataURL);
  };

  const handleCancelar = () => {
    setModalAbierto(false);
  };

  return (
    <div style={{ marginBottom: 20 }}>
      <label style={{ fontSize: 13, fontWeight: 600, color: '#8A9994',
                      letterSpacing: 0.4, display: 'block', marginBottom: 8 }}>
        {label}
      </label>
      <button
        type="button"
        onClick={() => setModalAbierto(true)}
        style={{
          width: '100%', padding: '18px 16px',
          background: firmada ? 'rgba(15,110,86,0.12)' : '#0D1311',
          border: `2px dashed ${firmada ? '#0f6e56' : '#2E4A40'}`,
          borderRadius: 12, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          color: firmada ? '#4CAF50' : '#6B7C74',
          fontSize: 14, fontWeight: 600,
          transition: 'all 0.2s',
        }}
      >
        {firmada
          ? '✅ Firma registrada — clic para editar'
          : '✏ Tocar para firmar'}
      </button>

      {modalAbierto && (
        <ModalFirma
          label={label}
          onGuardar={handleGuardar}
          onCancelar={handleCancelar}
        />
      )}
    </div>
  );
}
